/* eslint-disable no-unused-vars */
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { NavLink, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Avatar, Breadcrumbs } from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import HomeIcon from '@mui/icons-material/Home';

import { AirplaneTicketOutlined } from '@mui/icons-material';
import MainContent from '../components/MainContent';
import Modal from '../components/Modal';
import CloseTicketEmail from '../components/tickets/CloseTicketEmail';
import { axiosInstancev2 } from '../utils/Axios/axiosInterceptorv2';
import RightBar from '../components/RightBar';
import Calendar from '../components/calendar/Calendar';
import useParsedData from '../hooks/useParsedData';
import usePermissions from '../hooks/usePermissions';
import useGetUserRole from '../hooks/useGetUserRole';

function TicketDetail() {
  const navigate = useNavigate();

  const { ticketid } = useParams();
  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get('tab');
  const permissions = usePermissions();
  const role = useGetUserRole();
  const [emailModalOpen, setEmailModalOpen] = useState(false);

  const fetchTicket = async () => {
    const res = await axiosInstancev2.get(`support/tickets/${ticketid}`);
    return res;
  };

  const handleTicketClosed = () => {
    setEmailModalOpen(false);
    navigate(`/tickets${activeTab ? `?tab=${activeTab}` : ''}`);
  };

  const {
    data: ticketData,
    isLoading,
    isError,
  } = useQuery(['ticket'], fetchTicket, {
    staleTime: 3600,
    cacheTime: 3600 * 10000,
  });

  const ticket = ticketData?.data?.ticket;

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const month = date.toLocaleString('default', { month: 'short' });
    const year = date.getFullYear();
    const day = date.getDate();
    const time = date.toLocaleTimeString('en-US', { hour12: false });
    return `${month} ${day} ${year}, ${time}`;
  };

  return (
    <>
      <MainContent>
        <div className="flex justify-between bg-white px-2 py-5 rounded-lg">
          <Breadcrumbs aria-label="breadcrumb" separator={<NavigateNextIcon />}>
            <NavLink
              underline="hover"
              sx={{ display: 'flex', alignItems: 'center' }}
              color="inherit"
              to="/"
              className="flex items-center"
            >
              <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              <p>Home</p>
            </NavLink>
            <NavLink
              underline="hover"
              sx={{ display: 'flex', alignItems: 'center' }}
              color="inherit"
              to={`/tickets${activeTab ? `?tab=${activeTab}` : ''}`}
              className="flex items-center"
            >
              <AirplaneTicketOutlined sx={{ mr: 0.5 }} fontSize="inherit" />
              <p>Tickets</p>
            </NavLink>
          </Breadcrumbs>
        </div>
        <div className="bg-white mt-8 px-2 py-5 rounded-lg">
          <div className="flex justify-between items-center px-4">
            <div className="flex items-center mb-2">
              <Avatar sx={{ width: 50, height: 50 }}>
                {ticket?.user_full_name?.toUpperCase().split(' ')[0][0]}
              </Avatar>
              <div className="px-2 font-roboto text-md">
                <p className="capitalize">
                  <span className="font-bold">{ticket?.user_full_name}</span>
                  <span className="ml-2 text-claret-500">
                    &lt;{ticket?.user_email?.toLowerCase()}&gt;
                  </span>
                </p>
                <p className="mt-3 font-bold text-xs">
                  Posted {formatTimestamp(ticket?.created_at)}
                </p>
              </div>
            </div>
            {(permissions?.includes('UpdateTicket') ||
              role === 'SuperAdmin') && (
              <div className="flex space-x-2">
                <button
                  type="button"
                  className={` text-sm py-1 px-5 rounded-md  ${
                    (ticket?.status && ticket?.status) === 'CLOSED'
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-claret-500 text-white cursor-pointer'
                  }`}
                  onClick={() => setEmailModalOpen(true)}
                  disabled={ticket?.status === 'CLOSED'}
                >
                  Close
                </button>
              </div>
            )}
          </div>
          <div className="mt-5">
            <h2>
              <span className="mr-2 font-semibold">Subject: </span>{' '}
              {ticket?.subject
                ?.toLowerCase()
                .replace(/\b\w/g, (char) => char.toUpperCase())}
            </h2>
          </div>
          <div className="space-y-4 mt-5">
            <h2 className="font-semibold">Description</h2>
            {useParsedData(ticket?.description || '')}
          </div>
        </div>
      </MainContent>
      <RightBar>
        <Calendar />
      </RightBar>
      <Modal
        modalOpen={emailModalOpen}
        onClose={() => setEmailModalOpen(false)}
        title="Email and close ticket"
      >
        <CloseTicketEmail
          ticketId={ticketid}
          recipientName={ticket?.user_full_name}
          recipientEmail={ticket?.user_email}
          defaultSubject={ticket?.subject ? `Re: ${ticket.subject}` : ''}
          onCancel={() => setEmailModalOpen(false)}
          onClosed={handleTicketClosed}
        />
      </Modal>
    </>
  );
}

export default TicketDetail;
